<template>
  <div class="user-insights" :class="{ 'user-insights--dark': isDarkSkin }">

    <div class="ui-toolbar">
      <filter-card :filter-key="viewKey" dashboard single-row v-model="filter" v-bind:fields="filterFields"/>
      <!-- One-click department shortcuts: fills the business-unit filter with every unit of that department -->
      <div class="dept-quick" v-if="availableDepartments.length">
        <span class="dept-quick__label">{{ $t('filters.departments') }}</span>
        <button v-for="dept in availableDepartments" :key="dept" type="button"
                class="dept-quick__pill" :class="['dept-quick__pill--' + dept.toLowerCase(), { 'is-active': isDeptActive(dept) }]"
                @click="toggleDept(dept)">
          <i :class="isDeptActive(dept) ? 'fa-solid fa-check-circle' : 'fa-regular fa-circle'"></i>
          {{ $t(`businessUnitDepartments.${dept}`) }}
          <span class="dept-quick__count">{{ deptBuIds(dept).length }}</span>
        </button>
      </div>
      <calendar-bar v-model="dateRange" @input="onRangeChanged"/>
    </div>

    <!-- ============ CONTROLS (search + export) ============ -->
    <div class="ui-controls">
      <div class="ui-search">
        <feather-icon icon="SearchIcon" size="16" class="ui-search__icon"/>
        <input
            type="text"
            class="ui-search__input"
            v-model="searchText"
            :placeholder="$t('userInsights.searchUser')"
            @input="onSearchInput"
        />
        <button v-if="searchText" type="button" class="ui-search__clear" @click="clearSearch">
          <feather-icon icon="XIcon" size="14"/>
        </button>
      </div>

      <button type="button" class="ui-export" :disabled="exporting" @click="exportExcel">
        <b-spinner v-if="exporting" small class="mr-50"/>
        <i v-else class="fas fa-file-excel mr-50"></i>
        <span>{{ $t('userInsights.export') }}</span>
      </button>
    </div>

    <b-overlay :show="loading" rounded="lg" opacity="0.45" spinner-variant="primary">

      <!-- ============ PODIUM (top 3 — first page, no search) ============ -->
      <div class="ui-podium" v-if="podiumOrder.length">
        <div v-for="item in podiumOrder" :key="item.userId"
             class="ui-podium__card" :class="`ui-podium__card--p${item.rank}`"
             @click="openInsight(item)">
          <div class="ui-podium__medal">
            <span v-if="item.rank === 1">🥇</span>
            <span v-else-if="item.rank === 2">🥈</span>
            <span v-else>🥉</span>
          </div>
          <div class="ui-podium__rank">TOP {{ item.rank }}</div>
          <div class="ui-podium__name">
            {{ item.name }}
            <span v-for="t in (item.buTypes || [])" :key="t"
                  class="ui-sr" :class="t === 'SALES' ? 'ui-sr--s' : 'ui-sr--r'"
                  v-b-tooltip :title="t === 'SALES' ? $t('userInsights.sales') : $t('userInsights.retention')">
              {{ t === 'SALES' ? 'S' : 'R' }}
            </span>
          </div>
          <div class="ui-podium__org" v-if="orgName(item.orgId)">
            <i class="fas fa-building mr-25"></i>{{ orgName(item.orgId) }}
          </div>
          <div class="ui-podium__amount">{{ money(item.deposits) }}</div>
          <div class="ui-podium__chips">
            <span class="ui-chip ui-chip--light">
              <i class="fas fa-bolt mr-25"></i>{{ item.ftdCount }} {{ $t('userInsights.ftds') }} · {{ money(item.ftdAmount) }}
            </span>
            <span class="ui-chip ui-chip--light">
              {{ $t('userInsights.retention') }}: {{ money(item.retentionAmount) }}
            </span>
            <span class="ui-chip ui-chip--light">
              {{ item.clientCount }} {{ $t('userInsights.clients') }} · {{ item.callCount }} {{ $t('userInsights.calls') }}
            </span>
          </div>
          <div class="ui-podium__glow"></div>
        </div>
      </div>

      <!-- ============ RANKED LIST ============ -->
      <b-card no-body class="ui-list" v-if="listRows.length">
        <b-card-header class="pb-50">
          <h5 class="mb-0"><i class="fas fa-ranking-star mr-50"></i>{{ $t('userInsights.leaderboard') }}</h5>
        </b-card-header>
        <b-card-body class="pt-50">
          <div class="ui-tablewrap">
            <!-- One shared grid template for the header AND every row = perfect vertical alignment -->
            <div class="ui-grid ui-grid--head">
              <div></div>
              <div></div>
              <div class="ui-grid__h">{{ $t('userInsights.ftds') }}</div>
              <div class="ui-grid__h">{{ $t('userInsights.retention') }}</div>
              <div class="ui-grid__h">{{ $t('userInsights.withdrawals') }}</div>
              <div class="ui-grid__h">{{ $t('userInsights.net') }}</div>
              <div class="ui-grid__h">{{ $t('userInsights.clients') }} · {{ $t('userInsights.calls') }}</div>
              <div class="ui-grid__h ui-grid__h--total">{{ $t('userInsights.totalBrought') }}</div>
              <div></div>
            </div>
            <div v-for="item in listRows" :key="item.userId" class="ui-grid ui-grid--row" @click="openInsight(item)">
              <div class="ui-cell-rank">#{{ item.rank }}</div>
              <div class="ui-cell-who">
                <div class="ui-cell-who__name">
                  {{ item.name }}
                  <span v-for="t in (item.buTypes || [])" :key="t"
                        class="ui-sr" :class="t === 'SALES' ? 'ui-sr--s' : 'ui-sr--r'"
                        v-b-tooltip :title="t === 'SALES' ? $t('userInsights.sales') : $t('userInsights.retention')">
                    {{ t === 'SALES' ? 'S' : 'R' }}
                  </span>
                </div>
                <div class="ui-cell-who__sub">
                  <span v-if="item.username"><i class="fas fa-at mr-25"></i>{{ item.username }}</span>
                  <span v-if="orgName(item.orgId)" :class="{ 'ml-75': item.username }">
                    <i class="fas fa-building mr-25"></i>{{ orgName(item.orgId) }}
                  </span>
                </div>
                <div class="ui-cell-who__track"><div class="ui-cell-who__fill" :style="{ width: barWidth(item) }"></div></div>
              </div>
              <div class="ui-cell-num ui-cell-num--ftd">{{ item.ftdCount }} · {{ money(item.ftdAmount) }}</div>
              <div class="ui-cell-num ui-cell-num--ret">{{ money(item.retentionAmount) }}</div>
              <div class="ui-cell-num ui-cell-num--wd">{{ money(item.withdrawals) }}</div>
              <div class="ui-cell-num ui-cell-num--net">{{ money(item.net) }}</div>
              <div class="ui-cell-num ui-cell-num--mini">{{ item.clientCount }} · {{ item.callCount }}</div>
              <div class="ui-cell-num ui-cell-num--total">{{ money(item.deposits) }}</div>
              <feather-icon icon="ChevronRightIcon" size="18" class="ui-cell-go"/>
            </div>
          </div>
        </b-card-body>
      </b-card>

      <!-- ============ PAGINATION ============ -->
      <div class="ui-pager" v-if="!loading && items.length">
        <b-pagination
            v-if="totalRows > perPage"
            v-model="page"
            :total-rows="totalRows"
            :per-page="perPage"
            size="sm"
            class="mb-0"
            @change="onPageChange"
        />
        <b-select style="width: 75px" size="sm" v-model="perPage" @change="onSizeChange">
          <b-select-option :value="20">20</b-select-option>
          <b-select-option :value="50">50</b-select-option>
          <b-select-option :value="100">100</b-select-option>
        </b-select>
        <span class="ui-pager__info">{{ items.length }} {{ $t('userInsights.showingOf') }} {{ totalRows }}</span>
      </div>

      <!-- ============ EMPTY ============ -->
      <div v-if="!loading && !items.length" class="ui-empty">
        <i class="fas fa-medal ui-empty__icon"></i>
        <div class="ui-empty__title">{{ $t('userInsights.noData') }}</div>
        <div class="ui-empty__hint">{{ $t('userInsights.noDataHint') }}</div>
      </div>

    </b-overlay>

    <user-insight-modal ref="insightModal"/>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import CalendarBar from '@/components/widget/CalendarBar';
import FilterCard from '@/components/widget/FilterCard';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import UserInsightsFilterFields from '@/model/filter/UserInsightsFilterFields';
import UserInsightModal from '@/components/modal/UserInsightModal';

const REFRESH_DEBOUNCE_MS = 250;
const SEARCH_DEBOUNCE_MS = 500;
const PAGE_SIZE = 20;

export default {
  name: 'UserInsights',
  mixins: [mixinBase, mixinNotifications, mixinPermissions],
  components: { CalendarBar, FilterCard, UserInsightModal },
  data: () => ({
    filter: { orgIds: [], buIds: [], userIds: [] },
    filterFields: [],
    dateRange: { from: null, until: null },
    searchText: '',
    // Search actually applied to the loaded rows (set on fetch, not on keypress)
    // so the podium doesn't flicker away while the user is still typing.
    appliedSearch: '',
    page: 1,
    perPage: PAGE_SIZE,
    pagination: { page: 1, pages: 0, total: 0 },
    items: [],
    loading: false,
    exporting: false,
    refreshTimer: null,
    searchTimer: null,
  }),
  watch: {
    filter: { handler: 'onFilterChanged', deep: true },
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkSkin']),
    ...mapGetters('data', ['getOrganizationNameById', 'allBusinessUnits']),
    viewKey() { return this.$route.name || 'user-insights'; },
    // Departments that actually have business units (SALES / RETENTION / LANDING).
    availableDepartments() {
      const present = new Set((this.allBusinessUnits || []).map(b => b.type).filter(Boolean));
      return ['SALES', 'RETENTION', 'LANDING'].filter(t => present.has(t));
    },
    totalRows() { return (this.pagination && this.pagination.total) || this.items.length; },
    showPodium() { return this.page === 1 && !this.appliedSearch; },
    topThree() { return this.showPodium ? this.items.slice(0, 3) : []; },
    // Classic podium arrangement: 2nd - 1st - 3rd
    podiumOrder() {
      const three = this.topThree;
      if (three.length < 3) return three;
      return [three[1], three[0], three[2]];
    },
    listRows() { return this.showPodium ? this.items.slice(3) : this.items; },
    maxDeposit() { return Math.max(...this.items.map(i => i.deposits || 0), 0); },
  },
  created() {
    this.filterFields = UserInsightsFilterFields();
    this.dateRange = {
      from: this.$moment().startOf('month').format('YYYY-MM-DD'),
      until: this.$moment().endOf('month').format('YYYY-MM-DD'),
    };
  },
  mounted() { this.$root.$on('dashboard::refresh', this.scheduleRefresh); },
  beforeDestroy() {
    this.$root.$off('dashboard::refresh', this.scheduleRefresh);
    if (this.refreshTimer) { clearTimeout(this.refreshTimer); this.refreshTimer = null; }
    if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
  },
  methods: {
    ...mapActions('dashboard', ['getUserInsightsTop', 'downloadInsightsExcel']),

    // ---- Department quick-pick: one click selects ALL units of a department ----
    deptBuIds(dept) {
      let bus = (this.allBusinessUnits || []).filter(b => b.type === dept);
      const orgs = this.filter.orgIds || [];
      if (orgs.length) {
        bus = bus.filter(b => orgs.includes(b.orgId));
      }
      return bus.map(b => b.id);
    },
    isDeptActive(dept) {
      const ids = this.deptBuIds(dept);
      const sel = this.filter.buIds || [];
      return ids.length > 0 && ids.every(id => sel.includes(id));
    },
    toggleDept(dept) {
      const ids = this.deptBuIds(dept);
      if (!ids.length) return;
      const sel = new Set(this.filter.buIds || []);
      if (this.isDeptActive(dept)) {
        ids.forEach(id => sel.delete(id));
      } else {
        ids.forEach(id => sel.add(id));
      }
      this.filter.buIds = [...sel]; // deep watcher -> refresh; BU select shows them
    },

    money(v) { return this.convertToEURO(v || 0); },
    orgName(orgId) { return orgId ? this.getOrganizationNameById(orgId) : null; },
    barWidth(item) {
      if (!this.maxDeposit) return '0%';
      return Math.max(3, Math.round(((item.deposits || 0) / this.maxDeposit) * 100)) + '%';
    },

    buildPayload() {
      const search = this.searchText && this.searchText.trim() ? this.searchText.trim() : null;
      return {
        ...this.filter,
        range: { from: this.dateRange.from, until: this.dateRange.until },
        search,
        page: this.page,
        size: this.perPage,
      };
    },

    onFilterChanged() { this.page = 1; this.scheduleRefresh(); },
    onRangeChanged() { this.page = 1; this.scheduleRefresh(); },
    onSearchInput() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => { this.page = 1; this.scheduleRefresh(); }, SEARCH_DEBOUNCE_MS);
    },
    clearSearch() {
      if (this.searchTimer) { clearTimeout(this.searchTimer); this.searchTimer = null; }
      this.searchText = '';
      this.page = 1;
      this.scheduleRefresh();
    },
    onPageChange(page) {
      this.page = page;
      this.scheduleRefresh();
    },
    // Page-size select: restart from page 1 with the new size
    onSizeChange() {
      this.page = 1;
      this.scheduleRefresh();
    },

    scheduleRefresh() {
      if (this.refreshTimer) clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.refresh(), REFRESH_DEBOUNCE_MS);
    },
    refresh() {
      if (!this.dateRange.from) return;
      this.loading = true;
      const payload = this.buildPayload();
      this.getUserInsightsTop(payload)
          .then(res => {
            const r = res || {};
            this.items = r.data || [];
            this.pagination = r.pagination || { page: this.page, pages: 0, total: this.items.length };
            this.appliedSearch = payload.search || '';
          })
          .catch(err => this.showServerErrorNotification('fetchDataError', 'userInsights', err))
          .finally(() => { this.loading = false; });
    },

    exportExcel() {
      if (this.exporting) return;
      this.exporting = true;
      const filter = this.buildPayload();
      delete filter.page;
      delete filter.size;
      this.downloadInsightsExcel({ kind: 'users', filter })
          .catch(err => this.showServerErrorNotification('downloadFileError', 'userInsights', err))
          .finally(() => { this.exporting = false; });
    },

    openInsight(item) {
      // Pass the page's date range so the modal stats/drills match the filter
      this.$refs.insightModal.show(item.userId, item.rank,
          { from: this.dateRange.from, until: this.dateRange.until });
    },
  },
};
</script>

<style scoped lang="scss">
.user-insights {
  .ui-toolbar { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1rem; }
}

/* ===== Controls (search + export) ===== */
.ui-controls {
  display: flex; align-items: center; gap: 0.75rem;
  margin-bottom: 1rem; flex-wrap: wrap;
}
.ui-search {
  position: relative; flex: 1; min-width: 220px; max-width: 380px;

  &__icon {
    position: absolute; left: 0.8rem; top: 50%; transform: translateY(-50%);
    color: #9a9dad; pointer-events: none;
  }
  &__input {
    width: 100%; height: 38px; border-radius: 999px;
    border: 1px solid rgba(110, 117, 159, 0.24);
    background: #fff; color: #5e5873;
    padding: 0 2.2rem 0 2.4rem; font-size: 0.9rem;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:focus { outline: none; border-color: #7367f0; box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12); }
    &::placeholder { color: #b4b7bd; }
  }
  &__clear {
    position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%);
    width: 24px; height: 24px; border-radius: 50%; border: none; cursor: pointer; padding: 0;
    display: inline-flex; align-items: center; justify-content: center;
    background: rgba(110, 117, 159, 0.12); color: #6e6b7b;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover { background: rgba(234, 84, 85, 0.15); color: #ea5455; }
  }
}
.ui-export {
  display: inline-flex; align-items: center; margin-left: auto;
  border: none; border-radius: 999px; cursor: pointer;
  padding: 0 1.25rem; height: 38px;
  color: #fff; font-weight: 700; font-size: 0.85rem;
  background: linear-gradient(135deg, #34d399, #059669);
  box-shadow: 0 8px 18px -10px rgba(5, 150, 105, 0.7);
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 12px 24px -10px rgba(5, 150, 105, 0.8);
    filter: brightness(1.04);
  }
  &:active:not(:disabled) { transform: translateY(0); }
  &:disabled { opacity: 0.65; cursor: not-allowed; }
}

/* ===== Podium ===== */
.ui-podium {
  display: grid;
  grid-template-columns: 1fr 1.25fr 1fr;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1.25rem;

  &__card {
    position: relative;
    border-radius: 20px;
    padding: 1.4rem 1.25rem 1.25rem;
    color: #fff;
    cursor: pointer;
    overflow: hidden;
    text-align: center;
    box-shadow: 0 14px 30px -16px rgba(20, 24, 50, 0.35);
    transition: transform 0.22s ease, box-shadow 0.22s ease;

    &:hover { transform: translateY(-5px); box-shadow: 0 20px 44px -18px rgba(20, 24, 50, 0.45); }

    &--p1 {
      background: linear-gradient(150deg, #f6c744 0%, #eda912 55%, #c98a00 100%);
      padding-top: 2rem;
      padding-bottom: 1.8rem;
    }
    &--p2 { background: linear-gradient(150deg, #b8c2d8 0%, #8e9bb5 55%, #6e7c99 100%); }
    &--p3 { background: linear-gradient(150deg, #e2a06c 0%, #c97e42 55%, #a75f28 100%); }
  }

  &__medal { font-size: 2.2rem; line-height: 1; margin-bottom: 0.35rem; }
  &__rank { font-size: 0.72rem; font-weight: 800; letter-spacing: 0.14em; opacity: 0.85; }
  &__name {
    font-size: 1.15rem; font-weight: 700; margin-top: 0.3rem;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  &__org { font-size: 0.78rem; opacity: 0.85; margin-top: 0.15rem; }
  &__amount { font-size: 1.7rem; font-weight: 800; margin-top: 0.6rem; letter-spacing: -0.01em; }
  &__chips { display: flex; justify-content: center; gap: 0.4rem; margin-top: 0.7rem; flex-wrap: wrap; }
  &__glow {
    position: absolute; top: -45%; right: -18%; width: 240px; height: 240px; border-radius: 50%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.28), transparent 70%); pointer-events: none;
  }
}

.ui-chip {
  display: inline-flex; align-items: center;
  padding: 0.22rem 0.6rem; border-radius: 999px;
  font-size: 0.72rem; font-weight: 700;
  &--light { background: rgba(255, 255, 255, 0.22); color: #fff; backdrop-filter: blur(4px); }
}

/* ===== Ranked list ===== */
.ui-list { border-radius: 16px; }
/* ===== Ranked list: a real grid — one column template shared by the header
   and every row, so all numbers align vertically and scan like a table ===== */
.ui-tablewrap { overflow-x: auto; }

.ui-grid {
  display: grid;
  grid-template-columns: 52px minmax(210px, 1fr) 150px 130px 130px 130px 105px 180px 26px;
  gap: 0 0.8rem;
  align-items: center;
  min-width: 1160px;

  &--head {
    padding: 0.15rem 0.5rem 0.45rem;
    border-bottom: 1px solid rgba(110, 117, 159, 0.14);
    margin-bottom: 0.25rem;
  }
  &__h {
    font-size: 0.62rem; font-weight: 800; text-transform: uppercase;
    letter-spacing: 0.09em; color: #9a9dad; text-align: right; white-space: nowrap;
    &--total { color: #7367f0; }
  }

  &--row {
    padding: 0.6rem 0.5rem;
    border-radius: 10px;
    cursor: pointer;
    transition: background 0.15s ease, transform 0.15s ease;

    &:nth-of-type(odd) { background: rgba(110, 117, 159, 0.045); }
    &:hover { background: rgba(115, 103, 240, 0.09); transform: translateX(3px); }
  }
}

.ui-cell-rank {
  height: 30px; border-radius: 9px;
  display: inline-flex; align-items: center; justify-content: center;
  background: rgba(115, 103, 240, 0.12); color: #7367f0;
  font-weight: 800; font-size: 0.8rem;
}
.ui-cell-who {
  min-width: 0;
  &__name { font-weight: 700; color: #5e5873; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  &__sub { font-size: 0.72rem; color: #9a9dad; margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  // Share-of-#1 bar lives with the identity, not between the numbers
  &__track {
    margin-top: 5px; height: 5px; max-width: 210px;
    border-radius: 999px; background: rgba(110, 117, 159, 0.14); overflow: hidden;
  }
  &__fill {
    height: 100%; border-radius: 999px;
    background: linear-gradient(90deg, #7367f0, #9e95f5);
    transition: width 0.5s ease;
  }
}
.ui-cell-num {
  text-align: right; font-weight: 700; font-size: 0.95rem; color: #6e6b7b;
  font-variant-numeric: tabular-nums; white-space: nowrap;

  &--ftd { color: #f6a821; }
  &--ret { color: #4da3ff; }
  &--wd { color: #ea5455; }
  &--net { color: #28c76f; }
  &--mini { font-size: 0.82rem; font-weight: 600; color: #9a9dad; }
  &--total { font-size: 1.18rem; font-weight: 800; color: #7367f0; }
}
.ui-cell-go { color: #b4b7bd; justify-self: end; }

/* ===== Pagination ===== */
.ui-pager {
  display: flex; align-items: center; gap: 0.75rem; margin-top: 1rem;
  &__info { font-size: 0.78rem; color: #9a9dad; }
}

/* ===== Empty state ===== */
.ui-empty {
  text-align: center; padding: 3.5rem 1rem; color: #9a9dad;
  &__icon { font-size: 2.4rem; opacity: 0.5; }
  &__title { font-weight: 700; margin-top: 0.8rem; font-size: 1.05rem; }
  &__hint { font-size: 0.82rem; margin-top: 0.2rem; }
}

/* ===== Dark mode ===== */
.user-insights--dark {
  .ui-search__input {
    background: #283046; border-color: rgba(255, 255, 255, 0.12); color: #d0d2d6;
    &::placeholder { color: #676d7d; }
  }
  .ui-search__clear { background: rgba(255, 255, 255, 0.08); color: #b4b7bd; }
  .ui-grid--head { border-bottom-color: rgba(255, 255, 255, 0.1); }
  .ui-grid--row {
    &:nth-of-type(odd) { background: rgba(255, 255, 255, 0.03); }
    &:hover { background: rgba(158, 149, 245, 0.12); }
  }
  .ui-cell-who__name { color: #d0d2d6; }
  .ui-cell-who__track { background: rgba(255, 255, 255, 0.1); }
  // Neutral values need lighter ink on dark; accents re-asserted below
  .ui-cell-num { color: #d0d2d6;
    &--ftd { color: #f6a821; }
    &--ret { color: #4da3ff; }
    &--wd { color: #ea5455; }
    &--net { color: #28c76f; }
    &--mini { color: #9a9dad; }
    &--total { color: #9e95f5; }
  }
}

@media (max-width: 992px) {
  .ui-podium { grid-template-columns: 1fr; align-items: stretch;
    &__card--p1 { order: -1; }
  }
  /* Narrow screens: the grid keeps its shape and scrolls horizontally */
  .ui-search { max-width: none; }
}

/* ===== S / R department badges next to agent names ===== */
.ui-sr {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 6px;
  font-size: 0.62rem;
  font-weight: 800;
  margin-left: 0.3rem;
  vertical-align: middle;
  color: #fff;

  &--s { background: linear-gradient(135deg, #34d399, #059669); }
  &--r { background: linear-gradient(135deg, #4da3ff, #0062cc); }
}
.ui-podium__name .ui-sr { box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25); }

/* ===== Department quick pills (same look as the sales dashboard) ===== */
.dept-quick {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4rem;
  padding: 0.6rem 0.85rem;
  background: linear-gradient(135deg, rgba(0, 123, 255, 0.06), rgba(0, 123, 255, 0.02));
  border: 1px solid rgba(0, 123, 255, 0.12);
  border-radius: 14px;

  &__label {
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #b4b7bd;
    padding-right: 0.5rem;
    border-right: 1px solid rgba(110, 117, 159, 0.18);
    margin-right: 0.35rem;
  }

  &__pill {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.38rem 0.85rem;
    min-height: 32px;
    border-radius: 999px;
    border: 1px solid transparent;
    background: transparent;
    color: #6e6b7b;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: pointer;
    outline: none;
    white-space: nowrap;
    transition: all 0.18s ease;

    &--sales:hover { background: rgba(16, 185, 129, 0.1); color: #10b981; }
    &--retention:hover { background: rgba(0, 123, 255, 0.1); color: #007BFF; }
    &--landing:hover { background: rgba(245, 158, 11, 0.12); color: #d97706; }

    &--sales.is-active {
      color: #fff;
      background: linear-gradient(135deg, #34d399 0%, #10b981 60%, #059669 100%);
      box-shadow: 0 6px 14px -6px rgba(16, 185, 129, 0.55);
    }
    &--retention.is-active {
      color: #fff;
      background: linear-gradient(135deg, #4da3ff 0%, #007BFF 60%, #0062cc 100%);
      box-shadow: 0 6px 14px -6px rgba(0, 123, 255, 0.55);
    }
    &--landing.is-active {
      color: #fff;
      background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 60%, #d97706 100%);
      box-shadow: 0 6px 14px -6px rgba(245, 158, 11, 0.55);
    }
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 999px;
    background: rgba(110, 117, 159, 0.14);
    font-size: 0.68rem;
    font-weight: 700;
  }
  &__pill.is-active .dept-quick__count { background: rgba(0, 0, 0, 0.16); }
}

.user-insights--dark .dept-quick {
  background: linear-gradient(135deg, rgba(77, 163, 255, 0.10), rgba(77, 163, 255, 0.02));
  border-color: rgba(77, 163, 255, 0.18);

  .dept-quick__label { border-right-color: rgba(255, 255, 255, 0.08); }
  .dept-quick__pill { color: #b4b7bd; }
  .dept-quick__count { background: rgba(255, 255, 255, 0.1); }
}
</style>
